import "./App.css";
import { useEffect, useLayoutEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "./lib/api";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { DeliveryLocationProvider } from "./contexts/DeliveryLocationContext";
import { BottomNavigation } from "./components/BottomNavigation";
import { CartDrawer } from "./components/CartDrawer";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminLayout } from "./components/AdminLayout";
import QuickCommerceHome from "./pages/QuickCommerceHome";
import Category from "./pages/Category";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Checkout from "./pages/Checkout";
import MyOrders from "./pages/MyOrders";
import OrderTracking from "./pages/OrderTracking";
import AuthCallback from "./pages/AuthCallback";
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminCustomers = lazy(() => import("./pages/admin/Customers"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminDeliveryBoys = lazy(() => import("./pages/admin/DeliveryBoys"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminAreas = lazy(() => import("./pages/admin/Areas"));
const AdminPartnerStores = lazy(() => import("./pages/admin/PartnerStores"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminStoreInventory = lazy(() => import("./pages/admin/StoreInventory"));
const AdminSettlements = lazy(() => import("./pages/admin/Settlements"));
const AdminReconciliation = lazy(() => import("./pages/admin/Reconciliation"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const UnserviceableLeads = lazy(() => import("./pages/admin/UnserviceableLeads"));
const StorefrontControls = lazy(() => import("./pages/admin/StorefrontControls"));
const CategoriesManager = lazy(() => import("./pages/admin/CategoriesManager"));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications"));
const StorePortal = lazy(() => import("./pages/StorePortal"));
const RiderPortal = lazy(() => import("./pages/RiderPortal"));
import Categories from "./pages/Categories";
import ProductDetail from "./pages/ProductDetail";
import Account from "./pages/Account";
import Wishlist from "./pages/Wishlist";
import Cart from "./pages/Cart";
import Wallet from "./pages/Wallet";
import SelectLocation from "./pages/SelectLocation";
import Notifications from "./pages/Notifications";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const loc = useLocation();
  if (loc.hash && loc.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<StorefrontShell />}>
        <Route path="/" element={<QuickCommerceHome />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/c/:key" element={<Category />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/select-location" element={<SelectLocation />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <MyOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute>
              <OrderTracking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wishlist"
          element={
            <ProtectedRoute>
              <Wishlist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <Wallet />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="delivery-boys" element={<AdminDeliveryBoys />} />
        <Route path="partner-stores" element={<AdminPartnerStores />} />
        <Route path="store-inventory" element={<AdminStoreInventory />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="settlements" element={<AdminSettlements />} />
        <Route path="reconciliation" element={<AdminReconciliation />} />
        <Route path="areas" element={<AdminAreas />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="unserviceable-leads" element={<UnserviceableLeads />} />
        <Route path="storefront" element={<StorefrontControls />} />
        <Route path="categories" element={<CategoriesManager />} />
        <Route path="notifications" element={<AdminNotifications />} />
      </Route>
      <Route path="/store" element={<StorePortal />} />
      <Route path="/rider" element={<RiderPortal />} />
    </Routes>
  );
}

function StorefrontShell() {
  return (
    <>
      <div className="pb-24">
        <Outlet />
      </div>
      <BottomNavigation />
    </>
  );
}

// Smart, per-route scroll behaviour: a screen visited for the first time opens
// at the top; returning to a previously visited screen restores its last scroll
// position — like a native mobile app.
function ScrollManager() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const positions = useRef({});
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    const onScroll = () => {
      positions.current[pathRef.current] = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const saved = positions.current[pathname];
    // Restore on back/forward, or when returning to a screen seen earlier.
    if ((navType === "POP" || saved != null) && saved != null) {
      window.scrollTo(0, saved);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
}

function shade(hex, pct) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    let r = (n >> 16) + Math.round(2.55 * pct);
    let g = ((n >> 8) & 0xff) + Math.round(2.55 * pct);
    let b = (n & 0xff) + Math.round(2.55 * pct);
    const cl = (v) => Math.max(0, Math.min(255, v));
    return `#${((cl(r) << 16) | (cl(g) << 8) | cl(b)).toString(16).padStart(6, "0")}`;
  } catch {
    return hex;
  }
}

function ThemeApplier() {
  useEffect(() => {
    api
      .get("/business-info")
      .then((r) => {
        const primary = r.data?.theme?.primary;
        if (!primary) return;
        const dark = shade(primary, -12);
        let el = document.getElementById("qdn-theme");
        if (!el) {
          el = document.createElement("style");
          el.id = "qdn-theme";
          document.head.appendChild(el);
        }
        el.textContent = `
        .bg-orange-600{background-color:${primary} !important}
        .bg-orange-700,.hover\\:bg-orange-700:hover{background-color:${dark} !important}
        .text-orange-600{color:${primary} !important}
        .text-orange-700,.hover\\:text-orange-700:hover{color:${dark} !important}
        .border-orange-600{border-color:${primary} !important}
        .hover\\:border-orange-400:hover{border-color:${primary} !important}
      `;
      })
      .catch(() => {});
  }, []);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <DeliveryLocationProvider>
        <CartProvider>
          <WishlistProvider>
            <BrowserRouter>
              <ThemeApplier />
              <ScrollManager />
              <Toaster position="top-center" richColors />
              <Suspense fallback={<div className="grid min-h-screen place-items-center text-stone-500">Loading…</div>}>
                <AppRouter />
              </Suspense>
            </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </DeliveryLocationProvider>
    </AuthProvider>
  );
}
